/**
 * What a brand's campaign request needs next, in one sentence for lists of requests: the review,
 * the deposit and its deadline, or where the campaign stands once funded.
 */

import type { ReactNode } from "react";
import { Link } from "react-router";

import { styles } from "../components/ui.js";
import { formatUtc } from "../format.js";
import { refundAfterSeconds } from "./BrandFunds.js";
import { campaignPath } from "./campaigns.js";
import { type CampaignRequest, pendingChange, pendingTransfer } from "./requests.js";

export function requestNextStep(request: CampaignRequest, now = Date.now()): ReactNode {
  if (pendingTransfer(request)) {
    return "Offered to another wallet. It moves when that wallet takes it over.";
  }
  switch (request.status) {
    case "submitted":
      return "Morcast is reviewing your request.";
    case "accepted":
      if (pendingChange(request)) {
        return "Accepted, with your change waiting for review.";
      }
      return request.deposit
        ? `Accepted. Deposit the budget before ${formatUtc(request.deposit.depositBefore)}.`
        : "Accepted. Deposit the budget before the start.";
    case "funded":
      return fundedStep(request, now);
    case "rejected":
      return "Rejected. Open the request to read Morcast's note.";
    case "withdrawn":
      return "Withdrawn.";
    case "lapsed":
      return pendingChange(request)
        ? "Lapsed, with your new dates waiting for review."
        : "Lapsed. No deposit arrived before the start, so ask for new dates.";
  }
}

function fundedStep(request: CampaignRequest, now: number): ReactNode {
  const { campaign } = request;
  if (!campaign) {
    return "Funded.";
  }
  const link = (
    <Link to={campaignPath(campaign)} className={styles.link}>
      See the campaign
    </Link>
  );
  switch (campaign.status) {
    case "cancelled":
      return "Cancelled before the start. The escrow returned the budget.";
    case "refunded":
      return "Refunded. The escrow returned the budget.";
    case "settled":
      return <>Settled. {link} for its result.</>;
    case "funded":
      if (now < Date.parse(request.startAt)) {
        return (
          <>
            Funded and starting {formatUtc(request.startAt)}. {link}.
          </>
        );
      }
      if (now < Date.parse(request.endAt)) {
        return (
          <>
            Running until {formatUtc(request.endAt)}. {link}.
          </>
        );
      }
      if (now >= Date.parse(request.endAt) + refundAfterSeconds() * 1000) {
        return "Not settled by Day 10. Open the request to withdraw the whole budget.";
      }
      return <>Ended. Morcast is measuring the results. {link}.</>;
  }
}
